import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import "./ccbill-modal-custom.css";
import {
  Form,
  useFormValidation
} from '../../../shared/components/form';
import { charge as chargeCcbill } from '../../../shared/actions/ccbillAction';
import { updateUser, fetchUser } from '../../../shared/actions/userActions';
import { createAddCardSchema } from '../../../shared/validations/ccbill';
import {
  fetchAccountData,
  getDefaultFormValues,
  loadCCBillWidget,
  initializeFormWithUserData,
  createCCBillPaymentToken,
  saveCardToBackend
} from '../../../shared/utils/ccbillFormUtils';
import CheckoutSummaryCard from '../../../features/cards/CheckoutSummaryCard';
import PaymentLogos from './PaymentLogos';
import { useModalOpen } from '../../../shared/utils/modalUtils';
import BillingAddressForm from './BillingAddressForm';
import CardDetailsForm from './CardDetailsForm';
import { TRANSACTION_TYPE } from '../../../shared/constants/common';
import { reportError, reportWarning } from '../../../shared/sentry';

const CCBillModal = ({
  paymentForm,
  onSuccess,
  onCancel,
  openModal,
  toggleModal,
  creatorInfo,
  purchaseAmount,
  vatAmount = 0,
  vatPercentage = 0,
  purchasePayload = {},
  // Optional deferred mode: when no paymentForm exists yet (e.g. subscription
  // with no saved card), this is called on form submit — after the user has
  // filled the card form — to create the backend record and return its
  // paymentForm. It must resolve to { success, paymentForm?, error?, silent? }.
  // Existing flows that pass paymentForm up-front never trigger it.
  onBeforeCharge = null,
}) => {
  useModalOpen(openModal);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const dispatch = useDispatch();

  const form = useForm({
    resolver: zodResolver(createAddCardSchema(false)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getDefaultFormValues()
  });

  const {
    validateAndFocus,
    shouldShowError,
  } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const unsubscribeCalledRef = useRef(false);
  const paymentSuccessfulRef = useRef(false);
  const [isBrowser, setIsBrowser] = useState(false);

  // Reset form and state when modal opens/closes
  useEffect(() => {
    if (openModal) {
      // Reset all state variables
      setMessage('');
      setLoading(false);

      // Clear form errors
      form.clearErrors();
    }
  }, [openModal, form]);

  // Set isBrowser to true after mount (Safari compatibility)
  useEffect(() => {
    setIsBrowser(true);
  }, []);


  // Load CCBill Advanced Widget and initialize form
  useEffect(() => {
    if (openModal) {
      const loadData = async () => {
        const accountData = await fetchAccountData();
        setAccountData(accountData);

        // Load CCBill Advanced Widget
        loadCCBillWidget();

        initializeFormWithUserData(form, accountData).catch((err) => {
          reportError(err, 'CCBillModal init form');
          setMessage('⚠️ Unable to fetch location data. Please try again.');
        });
      };

      loadData();
    }
  }, [openModal, form]);



  const getValue = useCallback((name) => {
    return paymentForm?.find(f => f.name === name)?.value;
  }, [paymentForm]);

  const passThroughInfo = useMemo(() => {
    return paymentForm
      ? paymentForm.filter(f => f.name.startsWith('c_')).map(f => ({
        name: f.name,
        value: f.value
      }))
      : [];
  }, [paymentForm]);


  // Handle form submission
  const onSubmit = async (data) => {
    setMessage('');
    setLoading(true);

    try {
      // Deferred mode only: the card form has now been filled and submitted,
      // so it is safe to create the backend record (e.g. POST subscriptions)
      // and fetch its paymentForm. Nothing is created before this point.
      let effectivePaymentForm = paymentForm;
      if (!effectivePaymentForm && onBeforeCharge) {
        const prepared = await onBeforeCharge();
        if (!prepared?.success || !prepared?.paymentForm) {
          if (!prepared?.silent) {
            setMessage(`❌ ${prepared?.error || 'Failed to prepare payment. Please try again.'}`);
          }
          setLoading(false);
          return;
        }
        effectivePaymentForm = prepared.paymentForm;
      }

      const getEffectiveValue = (name) =>
        effectivePaymentForm?.find(f => f.name === name)?.value;
      const effectivePassThroughInfo = effectivePaymentForm
        ? effectivePaymentForm.filter(f => f.name.startsWith('c_')).map(f => ({
          name: f.name,
          value: f.value
        }))
        : passThroughInfo;

      // Step 1: Create CCBill payment token
      const tokenResult = await createCCBillPaymentToken();
      if (!tokenResult.success) {
        setMessage(`❌ ${tokenResult.error}`);
        setLoading(false);
        return;
      }

      const { paymentTokenId } = tokenResult;

      // Step 2: Prepare charge payload
      let chargePayload;
      if (effectivePaymentForm) {
        chargePayload = {
          paymentTokenId,
          clientSubacc: getEffectiveValue('clientSubacc'),
          currencyCode: Number(getEffectiveValue('currencyCode')),
          initialPrice: Number(getEffectiveValue('initialPrice')),
          initialPeriod: Number(getEffectiveValue('initialPeriod')),
          passThroughInfo: effectivePassThroughInfo
        };

        // Add conditional keys if they exist in paymentForm
        const recurringPrice = getEffectiveValue('recurringPrice');
        const recurringPeriod = getEffectiveValue('recurringPeriod');
        const numRebills = getEffectiveValue('numRebills');

        if (recurringPrice) chargePayload.recurringPrice = Number(recurringPrice);
        if (recurringPeriod) chargePayload.recurringPeriod = Number(recurringPeriod);
        if (numRebills) chargePayload.numRebills = Number(numRebills);
      } else {
        // Use the same structure as paymentForm but with default values
        chargePayload = {
          paymentTokenId,
          clientSubacc: process.env.REACT_APP_CCBILL_SUBACCOUNT,
          currencyCode: Number(process.env.REACT_APP_CCBILL_CURRENCY_CODE), // USD
          initialPrice: 9.99,
          initialPeriod: 30,
          passThroughInfo: []
        };
      }

      // Discount / offer: prefer client-calculated recurringPrice (realPrice + fee + VAT)
      if (purchasePayload?.recurringPrice != null) {
        chargePayload.recurringPrice = Number(purchasePayload.recurringPrice);
      }

      // Send charge request using action
      let chargeResponse;
      try {
        chargeResponse = await chargeCcbill(chargePayload);
      } catch (chargeError) {
        reportError(chargeError, 'CCBillModal charge');
        setMessage('❌ Payment processing failed. Please try again.');
        setLoading(false);
        return;
      }

      if (chargeResponse.success) {
        // Step 3: Update user with paymentTokenId via user action
        try {
          const updateResult = await dispatch(updateUser({ paymentTokenId }));
          if (!updateResult?.success) {
            reportWarning(updateResult, 'CCBillModal failed to update user with paymentTokenId');
            // Don't fail the payment if this step fails
          }
          await dispatch(fetchUser());
        } catch (updateErr) {
          reportWarning(updateErr, 'CCBillModal update user paymentTokenId');
          // Don't fail the payment if this step fails
        }

        // Step 4: Store card in /card API
        try {
          const cardResult = await saveCardToBackend({
            cardNumber: data.cardNumber,
            expMonth: data.expMonth,
            expYear: data.expYear,
            paymentTokenId,
            firstName: data?.firstName || '',
            lastName: data?.lastName || '',
            stateCode: data?.state || '',
            country: data?.country || ''
          }, { existingCardsCount: 0 });
          if (!cardResult.success) {
            reportError(cardResult?.error || cardResult?.message, 'CCBillModal store card');
            // Don't fail the payment if card saving fails
          }
        } catch (cardError) {
          reportError(cardError?.error || cardError?.message || cardError, 'CCBillModal store card');
          // Don't fail the payment if card saving fails
        }

        setMessage('✅ Payment successful!');

        // Reset form
        form.reset(getDefaultFormValues());

        // Set payment successful flag to prevent unsubscribe API call
        paymentSuccessfulRef.current = true;

        // Reset loading state
        setLoading(false);

        // Call onSuccess callback first to set success flag, then close modal
        if (onSuccess) {
          onSuccess();
        } else if (toggleModal) {
          // Fall back to toggleModal only when no success handler is provided
          setTimeout(() => {
            toggleModal();
          }, 100);
        }
      } else {
        const errorMessage = chargeResponse.error || chargeResponse.message || chargeResponse.declineText || 'Payment failed.';
        setMessage(`❌ ${errorMessage}`);
        setLoading(false);
      }
    } catch (error) {
      reportError(error, 'CCBillModal payment submit');
      setMessage('❌ Payment failed. Please try again.');
      setLoading(false);


    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateAndFocus();
    if (isValid) {
      form.handleSubmit(onSubmit)();
    } else {
      toast.error('❌ Please fix the errors in the form');
    }
  };


  // Function to call unsubscribe API and cancel purchase if purchase id is present
  const callUnsubscribeAPI = async (isBrowserClose = false) => {
    if (unsubscribeCalledRef.current || paymentSuccessfulRef.current) {
      return; // Prevent multiple calls or don't call if payment was successful
    }

    unsubscribeCalledRef.current = true;

    try {
      // Extract payment type from paymentForm
      const paymentType = paymentForm?.find(f => f.name === 'c_payment_type')?.value;

      // Extract creator ID from paymentForm
      const creatorId = paymentForm?.find(f => f.name === 'c_creator_id')?.value;

      // Extract purchase content ID from paymentForm
      const purchaseContentId = paymentForm?.find(f => f.name === 'c_purchase_content_id')?.value;

      // Extract tip ID from paymentForm
      const tipId = paymentForm?.find(f => f.name === 'c_tip_id')?.value;

      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      if (isBrowserClose) {
        let url = '';
        if (creatorId && paymentType === TRANSACTION_TYPE.SUBSCRIPTION) {
          url = `${process.env.REACT_APP_API}/content-creator/${creatorId}/subscriptions/true`;
        } else if (purchaseContentId && paymentType === TRANSACTION_TYPE.PURCHASE_CONTENT) {
          url = `${process.env.REACT_APP_API}/contents/${purchaseContentId}/purchase/true`;
        } else if (tipId && (paymentType === TRANSACTION_TYPE.TIP || paymentType === TRANSACTION_TYPE.MESSAGES_TIP)) {
          url = `${process.env.REACT_APP_API}/contents/${tipId}/tips/true`;
        } else if (purchaseContentId && paymentType === TRANSACTION_TYPE.WALLET_CREDIT_PURCHASE) {
          url = `${process.env.REACT_APP_API}/wallet/purchase/${purchaseContentId}/true`;
        }

        if (url) {
          try {
            // Use synchronous XHR (most reliable for browser close)
            const xhr = new XMLHttpRequest();
            xhr.open('DELETE', url, false); // false = synchronous
            xhr.setRequestHeader('Authorization', accessToken);
            xhr.send();
          } catch (error) {
            // If XHR fails, try sendBeacon as fallback
            if (navigator.sendBeacon) {
              // Send as GET request with token in URL
              const beaconUrl = `${url}?token=${encodeURIComponent(accessToken)}&beacon=true`;
              navigator.sendBeacon(beaconUrl);
            }
          }
        }
      }
    } catch (error) {
      reportError(error, 'CCBillModal unsubscribe API');
    }
  };

  // Handle browser close/back button and ESC key
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (openModal) {
        callUnsubscribeAPI(true);
      }
    };

    // Page exits/hidden or refresh
    const handlePageHide = (event) => {
      if (openModal) {
        callUnsubscribeAPI(true);
      }
    };

    // Back button
    const handlePopState = () => {
      if (openModal) {
        callUnsubscribeAPI(true);
      }
    };

    // Tab changes visibility
    // const handleVisibilityChange = () => {
    //   if (openModal && document.hidden) {
    //     callUnsubscribeAPI(true);
    //   }
    // };

    // ESC key
    // const handleKeyDown = (event) => {
    //   if (openModal && event.key === 'Escape') {
    //     callUnsubscribeAPI();
    //   }
    // };

    if (openModal) {
      // Browser close detection events - multiple fallbacks
      window.addEventListener('beforeunload', handleBeforeUnload); // User refreshes or closes page
      window.addEventListener('pagehide', handlePageHide); // Page exits/hidden or refresh  
      window.addEventListener('popstate', handlePopState); // Back button
      // document.addEventListener('visibilitychange', handleVisibilityChange);  // Tab changes visibility
      // document.addEventListener('keydown', handleKeyDown);  // ESC key

      // Reset the flags when modal opens
      unsubscribeCalledRef.current = false;
      paymentSuccessfulRef.current = false;
    }

    return () => {
      // Remove all event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload); // User refreshes or closes page
      window.removeEventListener('pagehide', handlePageHide); // Page exits/hidden or refresh
      window.removeEventListener('popstate', handlePopState); // Back button
      // document.removeEventListener('visibilitychange', handleVisibilityChange);  // Tab changes visibility
      // document.removeEventListener('keydown', handleKeyDown);  // ESC key  

      // Call unsubscribe API when component unmounts if modal was open and payment wasn't successful
      // if (openModal && !paymentSuccessfulRef.current) {
      //   callUnsubscribeAPI();
      // }
    };
  }, [openModal]);

  // Custom close function for the close button
  const handleCloseButton = () => {
    // Reset the flag after calling API so it can be called again next time
    setTimeout(() => {
      unsubscribeCalledRef.current = false;
    }, 1000);

    // Call cancel api and remove current data from the database (state-2)
    if (onCancel) {
      onCancel();
    }
  };

  // Toggle subscription details visibility
  const toggleSubscriptionDetails = () => {
    setShowSubscriptionDetails(!showSubscriptionDetails);
  };

  const modalRoot = (typeof document !== 'undefined' && document.body) || null;

  if (!openModal || !modalRoot || !isBrowser) {
    return null;
  }

  const modalMarkup = (
    <div className="ccbill-modal-root cm_bg_blur" >
      <div
        className="cc-bil-modal ccbill-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ccbill-modal-content">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="ccbill-form">
              {/* Subscription Summary */}
              <CheckoutSummaryCard
                creatorInfo={creatorInfo}
                paymentForm={paymentForm}
                purchaseAmount={purchaseAmount}
                vatAmount={vatAmount}
                showDetails={showSubscriptionDetails}
                onToggleDetails={toggleSubscriptionDetails}
                vatPercentage={vatPercentage}
              />

              {/* Payment Info Message - when no card on file, add card then we charge them */}
              <p className="cm_payment_info">
                {getValue('c_payment_type') === TRANSACTION_TYPE.TIP || getValue('c_payment_type') === TRANSACTION_TYPE.MESSAGES_TIP
                  ? 'Add your card to complete your tip with a single tap'
                  : getValue('c_payment_type') === TRANSACTION_TYPE.WALLET_CREDIT_PURCHASE
                    ? 'Add your card to complete your wallet credit purchase with a single tap'
                    : 'Add your card to unlock content with a single tap'}
              </p>

              {/* Billing Address Form */}
              <BillingAddressForm
                form={form}
                shouldShowError={shouldShowError}
                accountData={accountData}
              />

              {/* Card Details Form */}
              <CardDetailsForm
                form={form}
                shouldShowError={shouldShowError}
              />

              {/* Hidden Fields */}
              {(() => {
                const watchedValues = form.watch();
                return (
                  <>
                    <input
                      type="hidden"
                      name="ipAddress"
                      data-ccbill="ipAddress"
                      value={watchedValues.ipAddress}
                    />
                  </>
                );
              })()}

              {/* Action Buttons */}
              <div id="payment-actions" className="payment-actions">
                {/* Error message above submit button */}
                {message && (
                  <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="submit-button"
                >
                  {loading ? 'Processing...' : 'Complete Payment'}
                </button>
                {/* Back Button at Bottom */}
                <button
                  type="button"
                  className="cm-back-button"
                  onClick={handleCloseButton}
                >
                  Back
                </button>

                {/* Payment Logos */}
                <div id="payment-logos" className="payment-logos">
                  <PaymentLogos />
                </div>
              </div>

              {/* Footer */}
              <div id="payment-footer" className="payment-footer">
                <p>
                  <i className="fa-solid fa-shield-halved"></i>
                  Secure payment powered by CCBill
                </p>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalMarkup, modalRoot);
};

export default CCBillModal;